import { visualTier } from '../utils/tiers';
import { computeMultiplierScale } from '../game/constants';

export class RocketAnimation {
	private container: HTMLElement;
	private game: any;
	private currentMultiplier = 1.0;
	private flightPathElement: SVGPathElement | null = null;
	private animFrameId: number | null = null;
	private resizeObserver: ResizeObserver | null = null;
	private lastResizeTimer: any = null;
	private crashMoveTimer: number | null = null;
	private crashFinalizeRaf: number | null = null;

	private rocketElement: HTMLElement;
	private multiplierDisplay: HTMLElement;
	private multiplierValue: HTMLElement;
	private flamesElement: HTMLElement;

	private cycleStartTime = 0;
	private readonly cyclePeriod = 8;

	private readonly topFactor = 0.25;
	private readonly bottomFactor = 0.55;
	private readonly safeMargin = 8;

	constructor(container: HTMLElement, game: any) {
		this.container = container;
		this.game = game;

		this.rocketElement = this.resolve('#rocket');
		this.multiplierDisplay = this.resolve('#multiplier-display');
		this.multiplierValue = this.resolve('#multiplier-value');
		this.flamesElement = this.resolve('#flames');
		this.flightPathElement = document.querySelector('#flight-path') as SVGPathElement | null;

		this.initialize();
	}

	private resolve(selector: string): HTMLElement {
		const el = document.querySelector(selector) || this.container.querySelector(selector);
		if (!el) throw new Error(`Required element "${selector}" not found`);
		return el as HTMLElement;
	}

	private debouncedRecalcPathMetrics = () => {
		if (this.lastResizeTimer) window.clearTimeout(this.lastResizeTimer);
		this.lastResizeTimer = window.setTimeout(() => {
			this.recalcPathMetrics();
			this.lastResizeTimer = null;
		}, 120);
	};

	private initialize() {
		this.setupGameListeners();
		this.updateMultiplier(1.0);

		try {
			if (this.game.getCurrentState?.() === 'BET_TIME') this.resetRocket(true);
		} catch {}

		this.cycleStartTime = performance.now();
		this.animFrameId = requestAnimationFrame(this.renderLoop.bind(this));
		this.recalcPathMetrics();
		window.addEventListener('resize', this.debouncedRecalcPathMetrics);
	}

	private computeSafeBand(h: number): { top: number; bottom: number } {
		const rocketH = this.rocketElement.getBoundingClientRect().height || 0;
		const half = rocketH / 2;
		return {
			top: Math.max(h * this.topFactor, half + this.safeMargin),
			bottom: Math.min(h * this.bottomFactor, h - half - this.safeMargin),
		};
	}

	private recalcPathMetrics() {
		if (!this.flightPathElement) return;
		const rect = this.container.getBoundingClientRect();
		const x = rect.width / 2;
		const band = this.computeSafeBand(rect.height);
		const travel = Math.max(40, band.bottom - band.top);
		const y1 = band.bottom - travel * 0.33;
		const y2 = band.bottom - travel * 0.66;
		this.flightPathElement.setAttribute('d', `M${x},${band.bottom} C${x},${y1} ${x},${y2} ${x},${band.top}`);
		try { this.flightPathElement.getTotalLength(); } catch {}
	}

	private setupGameListeners() {
		this.game.on('multiplierUpdate', () => {
			const m = this.game.getCurrentMultiplier?.();
			if (m) this.updateMultiplier(m);
		});
		this.game.on('rocketLaunch', () => this.launchRocket());
		this.game.on('milestone', () => {
			const m = this.game.getCurrentMultiplier?.();
			if (m) this.triggerMilestone(m);
		});
		this.game.on('crash', (e: any) => this.crashRocket(e?.data?.multiplier));
		this.game.on('stateChange', (e: any) => {
			if (e?.data?.state) this.handleStateChange(e.data.state);
		});
	}

	updateMultiplier(multiplier: number) {
		this.currentMultiplier = multiplier;
		try { if ((window as any).__MemePump_SolidPhase5 === true) return; } catch {}
		this.multiplierValue.textContent = multiplier.toFixed(2);
		const scale = computeMultiplierScale(multiplier);
		this.multiplierDisplay.style.setProperty('--mult-base-scale', scale.toFixed(3));
		this.multiplierDisplay.setAttribute('data-tier', visualTier(multiplier));
		this.multiplierDisplay.classList.add('updating');
		setTimeout(() => this.multiplierDisplay.classList.remove('updating'), 100);
	}

	launchRocket() {
		this.rocketElement.classList.remove('crashing');
		this.rocketElement.classList.add('launching');
	}

	private renderLoop() {
		this.animFrameId = requestAnimationFrame(this.renderLoop.bind(this));

		const elapsed = (performance.now() - this.cycleStartTime) / 1000;
		const rect = this.container.getBoundingClientRect();
		const band = this.computeSafeBand(rect.height);
		const travel = Math.max(20, band.bottom - band.top);
		const progress = (Math.sin(elapsed * (Math.PI * 2 / this.cyclePeriod)) + 1) / 2;
		const yPos = band.top + travel * (1 - progress);
		const xPos = rect.width * 0.5;

		this.rocketElement.style.position = 'absolute';
		this.rocketElement.style.left = `${xPos}px`;
		this.rocketElement.style.top = `${yPos}px`;
		this.rocketElement.style.transform = 'translate(-50%, -50%) scale(var(--rocket-scale, 1))';
	}

	private triggerMilestone(multiplier: number) {
		this.rocketElement.classList.add('shaking');
		setTimeout(() => this.rocketElement.classList.remove('shaking'), 500);
		this.showMilestoneBanner(multiplier);
	}

	private showMilestoneBanner(multiplier: number) {
		let stack = this.container.querySelector('.milestone-stack') as HTMLElement | null;
		if (!stack) {
			stack = document.createElement('div');
			stack.className = 'milestone-stack';
			this.container.appendChild(stack);
		}
		Array.from(stack.querySelectorAll('.milestone-banner')).forEach(b => b.remove());

		const { phrases, icon, tierClass } = this.getMilestoneConfig(multiplier);
		const message = phrases[Math.floor(Math.random() * phrases.length)];

		const banner = document.createElement('div');
		banner.className = `milestone-banner hype${tierClass ? ` ${tierClass}` : ''}`;
		banner.setAttribute('data-mult', multiplier.toFixed(2));
		banner.innerHTML = `<span class="ms-icon">${icon}</span><span class="ms-text">${message}</span>`;
		stack.appendChild(banner);

		requestAnimationFrame(() => banner.classList.add('show'));

		setTimeout(() => {
			banner.classList.add('hide');
			banner.classList.remove('show');
			setTimeout(() => banner.remove(), 560);
		}, 2200);
	}

	private getMilestoneConfig(multiplier: number): { phrases: string[]; icon: string; tierClass: string } {
		if (multiplier >= 100) return { phrases: ['GALACTIC OVERDRIVE', 'COSMIC RIFT', 'MOONLESS MODE', 'REALITY BREAK'], icon: '🚀', tierClass: 'tier-epic' };
		if (multiplier >= 50) return { phrases: ['LIGHTSPEED FRENZY', 'STARFIRE SURGE', 'ORBIT BREACH', 'NEBULA RAGE'], icon: '🔥', tierClass: 'tier-epic' };
		if (multiplier >= 25) return { phrases: ['ION STORM', 'PLASMA VEIL', 'SKY RIP', 'VELOCITY SPIKE'], icon: '⚡', tierClass: 'tier-high' };
		if (multiplier >= 10) return { phrases: ['HYPER CLIMB', 'DIAMOND BURN', 'CORE MELT', 'PLASMA ASCENT'], icon: '💎', tierClass: 'tier-high' };
		if (multiplier >= 5) return { phrases: ['TURBO BOOST', 'WARP UP', 'BOOST CHAIN', 'THRUST ROLL'], icon: '🎯', tierClass: '' };
		if (multiplier >= 2) return { phrases: ['ASCENT', 'RISING', 'UPWARD'], icon: '📈', tierClass: '' };
		return { phrases: ['BOOST'], icon: '✨', tierClass: '' };
	}

	private crashRocket(_finalMult?: number) {
		this.rocketElement.classList.remove('ascending');
		this.rocketElement.classList.add('crashing');
		this.rocketElement.style.opacity = '0';
		this.rocketElement.style.visibility = 'hidden';
		this.multiplierDisplay.classList.add('crashed');

		this.cancelCrashFinalize();
		this.crashMoveTimer = window.setTimeout(() => {
			this.multiplierDisplay.classList.add('crash-move-init');
			this.crashFinalizeRaf = requestAnimationFrame(() => {
				this.multiplierDisplay.classList.add('crash-final');
				this.fitCrashFinalText();
			});
		}, 420);
	}

	private fitCrashFinalText() {
		const viewportW = window.innerWidth || document.documentElement.clientWidth || 360;
		const sidePadding = viewportW <= 480 ? 48 : 64;
		const maxWidth = Math.max(220, viewportW - sidePadding);

		const raw = (this.multiplierValue.textContent || '').trim();
		const digits = raw.replace(/[^0-9]/g, '').length;
		let fontScale = 1;

		if (digits >= 8) fontScale = 0.68;
		else if (digits === 7) fontScale = 0.74;
		else if (digits === 6) fontScale = 0.82;
		else if (digits === 5) fontScale = 0.90;

		if (viewportW <= 480) fontScale *= 0.9;
		if (viewportW <= 380) fontScale *= 0.88;

		const finalScale = viewportW <= 380 ? 1.55 : viewportW <= 480 ? 1.65 : 1.85;
		this.multiplierDisplay.style.setProperty('--crash-final-scale', finalScale.toFixed(3));
		this.multiplierDisplay.style.setProperty('--crash-final-font-scale', fontScale.toFixed(3));

		requestAnimationFrame(() => {
			const rect = this.multiplierDisplay.getBoundingClientRect();
			if (rect.width <= maxWidth) return;
			const fitted = Math.max(0.54, fontScale * (maxWidth / rect.width) * 0.98);
			this.multiplierDisplay.style.setProperty('--crash-final-font-scale', fitted.toFixed(3));
		});
	}

	private cancelCrashFinalize() {
		if (this.crashMoveTimer != null) { clearTimeout(this.crashMoveTimer); this.crashMoveTimer = null; }
		if (this.crashFinalizeRaf != null) { cancelAnimationFrame(this.crashFinalizeRaf); this.crashFinalizeRaf = null; }
	}

	private resetRocket(hide: boolean = false) {
		this.rocketElement.classList.remove('launching', 'ascending', 'shaking', 'crashing');
		this.cancelCrashFinalize();
		this.multiplierDisplay.classList.remove('crashed', 'crash-final', 'crash-move-init');
		this.multiplierDisplay.style.removeProperty('--crash-final-scale');
		this.multiplierDisplay.style.removeProperty('--crash-final-font-scale');
		this.updateMultiplier(1.0);
		this.rocketElement.style.transform = 'translate(-50%, -50%) scale(var(--rocket-scale, 1))';
		this.rocketElement.classList.remove('pre-init-hidden');
		this.multiplierDisplay.classList.remove('pre-init-hidden');

		if (hide) {
			this.rocketElement.style.transition = 'none';
			this.multiplierDisplay.style.transition = 'none';
			this.rocketElement.style.opacity = '0';
			this.rocketElement.style.visibility = 'hidden';
			this.multiplierDisplay.style.opacity = '0';
		} else {
			this.rocketElement.style.opacity = '1';
			this.rocketElement.style.visibility = 'visible';
			this.multiplierDisplay.style.opacity = '1';
		}
	}

	private revealRocket() {
		this.rocketElement.style.transition = 'opacity 420ms ease';
		this.multiplierDisplay.style.transition = 'opacity 420ms ease';
		this.rocketElement.classList.remove('pre-init-hidden');
		this.multiplierDisplay.classList.remove('pre-init-hidden');
		this.rocketElement.style.visibility = 'visible';
		requestAnimationFrame(() => {
			this.rocketElement.style.opacity = '1';
			this.multiplierDisplay.style.opacity = '1';
		});
	}

	private handleStateChange(state: string) {
		switch (state) {
			case 'BET_TIME':
				this.resetRocket(true);
				break;
			case 'ROUND_START':
				this.clearCrashState();
				this.revealRocket();
				break;
			case 'RUNNING':
				this.clearCrashState();
				break;
		}
	}

	public clearCrashState() {
		this.cancelCrashFinalize();
		this.multiplierDisplay.classList.remove('crashed', 'crash-final', 'crash-move-init');
		this.multiplierDisplay.style.removeProperty('--crash-final-scale');
		this.multiplierDisplay.style.removeProperty('--crash-final-font-scale');
	}

	getCurrentMultiplier() { return this.currentMultiplier; }

	destroy() {
		if (this.animFrameId) cancelAnimationFrame(this.animFrameId);
		this.cancelCrashFinalize();
		this.rocketElement.classList.remove('launching', 'ascending', 'shaking', 'crashing');
		this.multiplierDisplay.classList.remove('updating');
		window.removeEventListener('resize', this.debouncedRecalcPathMetrics);
		if (this.resizeObserver) { this.resizeObserver.disconnect(); this.resizeObserver = null; }
	}
}
